"use client";

import { useServerRenderedProductTiles } from "@faire/retailer-visitor-shared/app/(app)/retailer/[locale]/(pagesWithModal)/(defaultHeader)/search/(treatment)/rsc/_components/ProductTile/ServerRenderedProductTilesContext";
import { ProductTile } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/ProductTile";
import { ProductTileType } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/ProductTileType";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { trackSearchProductBatchView } from "@faire/web-api--source/events/search/view/productBatch";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import React, { use } from "react";

export const MaybeServerRenderedProductTileWrapper = ({
  index,
  productTile,
  brand,
  requestId,
  productTokens,
}: {
  index: number;
  productTile: ILeanProductTile | undefined;
  brand: ILeanProductTile.IBrand | undefined;
  requestId: string | undefined;
  productTokens: (string | undefined)[] | undefined;
}) => {
  const viewRef = useDoActionWhenVisible(() => {
    const productToken = productTokens?.[index] ?? productTile?.product?.token;
    if (productToken) {
      trackSearchProductBatchView(requestId ?? "", productToken);
    }
  });

  return (
    <div ref={viewRef}>
      <MaybeServerRenderedProductTileWrapperBase
        index={index}
        productTile={productTile}
        brand={brand}
      />
    </div>
  );
};

const MaybeServerRenderedProductTileWrapperBase = ({
  index,
  productTile,
  brand,
}: {
  index: number;
  productTile: ILeanProductTile | undefined;
  brand: ILeanProductTile.IBrand | undefined;
}) => {
  const isLoggedInRetailer = useIsLoggedInRetailer();
  if (!isLoggedInRetailer) {
    return (
      <ProductTile
        brand={brand}
        productTile={productTile}
        productTileType={ProductTileType.SEARCH_PAGE_PRODUCT_TILE}
      />
    );
  }

  return (
    <ServerRenderedProductTileWrapper
      index={index}
      productTile={productTile}
      brand={brand}
    />
  );
};

const ServerRenderedProductTileWrapper = ({
  index,
  productTile,
  brand,
}: {
  index: number;
  productTile: ILeanProductTile | undefined;
  brand: ILeanProductTile.IBrand | undefined;
}) => {
  // Slot the RSC Product Tile by returning the context value
  // Note that useServerRenderedProductTiles returns an array of RSC Product Tiles
  // If the product tile is not provided for the index, then return the child client tile
  const BATCH_SIZE = 12;
  const serverRenderedProductTiles = useServerRenderedProductTiles();

  let serverRenderedProductTile;
  if (index < BATCH_SIZE) {
    serverRenderedProductTile =
      serverRenderedProductTiles?.firstServerRenderedProductTilesBatch
        ?.serverRenderedProductTiles?.[index];
  } else {
    const remainingTilesPromise =
      serverRenderedProductTiles?.remainingServerRenderedProductTiles;
    if (remainingTilesPromise) {
      const remainingTiles = use(remainingTilesPromise);
      serverRenderedProductTile =
        remainingTiles!.serverRenderedProductTiles![index - BATCH_SIZE];
    }
  }

  return (
    <>
      {serverRenderedProductTile ?? (
        <ProductTile
          brand={brand}
          productTile={productTile}
          productTileType={ProductTileType.SEARCH_PAGE_PRODUCT_TILE}
        />
      )}
    </>
  );
};
